import PropTypes from 'prop-types'
import React from 'react'
import { FormattedNumber } from 'react-intl'
import classNames from 'classnames'

import { toRoman } from 'helpers'
import {
    ELO_RATING_TEXT,
    LESS_THAN_REQUIRED_BATTLES_COUNT,
    RATING_POSITION,
    SORTIES,
} from 'helpers/i18n.jsx'

import {
    STATS_TOOLTIP_28_DAYS_STATS,
    STATS_TOOLTIP_ALL_TIME_STATS,
    STATS_TOOLTIP_FORT_BATTLES,
    STATS_TOOLTIP_FORT_BATTLES_COUNT,
    STATS_TOOLTIP_HEADER,
    STATS_TOOLTIP_NO_FORT_BATTLES,
    STATS_TOOLTIP_NO_SORTIES,
    STATS_TOOLTIP_NO_STATS,
    STATS_TOOLTIP_SORTIES_COUNT,
    STATS_TOOLTIP_WIN_RATE,
} from './constants.jsx'

import themeTooltip from './StatsTable.tooltip.scss'
import themeExternal from './StatsTable.external.scss'


const THEMES = {
    tooltip: 'tooltip',
    external: 'external',
}

const StatsTable = (props) => {
    const { elo, sorties, fort_battles: fortBattles, level, elo_details } = props

    const themes = {
        [THEMES.tooltip]: themeTooltip,
        [THEMES.external]: themeExternal,
    }
    const styles = themes[props.theme]

    const hasSorties = sorties.battles_count
    const hasFortBattles = fortBattles && fortBattles.battles_count
    const fortBattlesUnavailable = !fortBattles

    const tableTitleClassName = (cell) => classNames(styles[cell], styles[`${cell}__title`], styles[`${cell}__left`])
    const tableValuesClassName = (cell) => classNames(styles[cell], styles[`${cell}__values`], styles[`${cell}__right`])

    let ratingMessage
    if (!elo_details) {
        ratingMessage = null
    } else if (elo_details.position && elo_details.position !== null) {
        const message = RATING_POSITION(
            <span className={styles.descriptionValue}>
                <FormattedNumber value={elo_details.position} />
            </span>
        )
        ratingMessage = (
            <p className={styles.description}>
                {message}
            </p>
        )
    } else {
        const message = LESS_THAN_REQUIRED_BATTLES_COUNT(
            <FormattedNumber value={elo_details.battles_count} />,
            <FormattedNumber value={elo_details.required_battles_count} />
        )
        ratingMessage = (
            <p className={styles.description}>
                {message}
            </p>
        )
    }

    const noSortiesNode = (
        <div>
            <p className={styles.noBattlesTitle}>{SORTIES}</p>
            <p className={styles.noBattles}>{STATS_TOOLTIP_NO_SORTIES(toRoman(level))}</p>
        </div>
    )

    const sortiesNode = hasSorties ? (
        <div className={styles.table}>
            <div className={styles.thead}>
                <div className={styles.tr}>
                    <div className={tableTitleClassName('th')}>{SORTIES}</div>
                    <div className={tableValuesClassName('th')}>{STATS_TOOLTIP_ALL_TIME_STATS}</div>
                    <div className={tableValuesClassName('th')}>{STATS_TOOLTIP_28_DAYS_STATS}</div>
                </div>
            </div>

            <div className={styles.tbody}>
                <div className={styles.tr}>
                    <div className={tableTitleClassName('td')}>
                        {STATS_TOOLTIP_SORTIES_COUNT}
                    </div>
                    <div className={tableValuesClassName('td')}>
                        <FormattedNumber value={sorties.battles_count} />
                    </div>
                    <div className={tableValuesClassName('td')}>
                        <FormattedNumber value={sorties.battles_count_for_last_28_days} />
                    </div>
                </div>
                <div className={styles.tr}>
                    <div className={tableTitleClassName('td')}>
                        {STATS_TOOLTIP_WIN_RATE}
                    </div>
                    <div className={tableValuesClassName('td')}>
                        {`${sorties.wins_percent}%`}
                    </div>
                    <div className={tableValuesClassName('td')}>
                        {`${sorties.wins_percent_for_last_28_days}%`}
                    </div>
                </div>
            </div>
        </div>
    ) : noSortiesNode

    const noFortBattlesNode = !fortBattlesUnavailable ? (
        <div>
            <p className={styles.noBattlesTitle}>{STATS_TOOLTIP_FORT_BATTLES}</p>
            <p className={styles.noBattles}>{STATS_TOOLTIP_NO_FORT_BATTLES}</p>
        </div>
    ) : null

    const fortBattlesNode = hasFortBattles ? (
        <div className={styles.table}>

            <div className={styles.thead}>
                <div className={styles.tr}>
                    <div className={tableTitleClassName('th')}>{STATS_TOOLTIP_FORT_BATTLES}</div>
                    <div className={tableValuesClassName('th')}>{STATS_TOOLTIP_ALL_TIME_STATS}</div>
                    <div className={tableValuesClassName('th')}>{STATS_TOOLTIP_28_DAYS_STATS}</div>
                </div>
            </div>

            <div className={styles.tbody}>
                <div className={styles.tr}>
                    <div className={tableTitleClassName('td')}>
                        {STATS_TOOLTIP_FORT_BATTLES_COUNT}
                    </div>
                    <div className={tableValuesClassName('td')}>
                        <FormattedNumber value={fortBattles.battles_count} />
                    </div>
                    <div className={tableValuesClassName('td')}>
                        <FormattedNumber value={fortBattles.battles_count_for_last_28_days} />
                    </div>
                </div>
                <div className={styles.tr}>
                    <div className={tableTitleClassName('td')}>
                        {STATS_TOOLTIP_WIN_RATE}
                    </div>
                    <div className={tableValuesClassName('td')}>
                        {`${fortBattles.wins_percent}%`}
                    </div>
                    <div className={tableValuesClassName('td')}>
                        {`${fortBattles.wins_percent_for_last_28_days}%`}
                    </div>
                </div>
            </div>

        </div>
    ) : noFortBattlesNode

    const divider = <div className={styles.divider} />

    if (!hasSorties && !hasFortBattles) {
        return (
            <div className={styles.base}>
                <p className={styles.title}>{STATS_TOOLTIP_HEADER(toRoman(level))}</p>
                <p className={styles.noBattles}>
                    {STATS_TOOLTIP_NO_STATS(toRoman(level))}
                </p>
            </div>
        )
    }

    return (
        <div className={styles.base}>
            <p className={styles.title}>
                {STATS_TOOLTIP_HEADER(toRoman(level))}
            </p>
            <p className={styles.description}>
                <span className={styles.descriptionTitle}>{ELO_RATING_TEXT}{': '}</span>
                <span className={styles.descriptionValue}><FormattedNumber value={elo} /></span>
            </p>
            {ratingMessage}
            {divider}
            {sortiesNode}
            {divider}
            {fortBattlesNode}
        </div>
    )
}

StatsTable.propTypes = {
    elo: PropTypes.number.isRequired,
    level: PropTypes.string.isRequired,
    fort_battles: PropTypes.shape({
        battles_count: PropTypes.number.isRequired,
        battles_count_for_last_28_days: PropTypes.number.isRequired,
        wins_percent: PropTypes.number.isRequired,
        wins_percent_for_last_28_days: PropTypes.number.isRequired,
    }),
    sorties: PropTypes.shape({
        battles_count: PropTypes.number.isRequired,
        battles_count_for_last_28_days: PropTypes.number.isRequired,
        wins_percent: PropTypes.number.isRequired,
        wins_percent_for_last_28_days: PropTypes.number.isRequired,
    }).isRequired,
    theme: PropTypes.oneOf(Object.values(THEMES)).isRequired,
    elo_details: PropTypes.shape({
        position: PropTypes.number,
        battles_count: PropTypes.number,
        required_battles_count: PropTypes.number,
    }),
}

StatsTable.defaultProps = {
    theme: THEMES.tooltip,
}

StatsTable.themes = {
    [THEMES.tooltip]: THEMES.tooltip,
    [THEMES.external]: THEMES.external,
}

export default StatsTable



// WEBPACK FOOTER //
// ./components/StatsTable/StatsTable.jsx